import React from "react";
import { ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/24/outline";
import { environment } from "../../../environment";
import { ScalarDocs } from "./ScalarDocs.component";

// Standalone, full-page API documentation route. Opened in its own browser
// window from the API Keys settings tab (replaces the former in-page dialog).
// Kept self-contained so it can render outside the authenticated Dashboard shell.

const externalBaseUrl = `${environment.v2Url}/external`;
const SPEC_URL = "/openapi/external-api.yaml";

// The static spec lists local/test/prod servers; Scalar defaults to the first one
// (localhost), so production docs would show the wrong base URL. Rewrite the
// `servers:` block to the single server for the current environment.
const buildEnvSpec = async (): Promise<string | null> => {
    const res = await fetch(SPEC_URL);
    if (!res.ok) return null;
    const text = await res.text();
    const description = environment.production
        ? 'Production'
        : environment.baseUrl.includes('test')
            ? 'Test'
            : 'Local development';
    const serversBlock = `servers:\n  - url: ${externalBaseUrl}\n    description: ${description}\n`;
    return text.replace(/^servers:\r?\n(?:[ \t]+.*\r?\n)+/m, serversBlock);
};

// #region CopyButton
const CopyButton: React.FunctionComponent<{ value: string; label?: string }> = ({ value, label }) => {
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-surface-strong dark:hover:bg-stone-700"
        >
            {copied ? <CheckIcon className="h-4 w-4 text-emerald-600" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
            {label && <span>{copied ? 'Copied' : label}</span>}
        </button>
    );
};

// #region CodeBlock
const CodeBlock: React.FunctionComponent<{ title: string; code: string }> = ({ title, code }) => (
    <div className="rounded-lg bg-surface-muted dark:bg-stone-800 p-3">
        <div className="mb-1 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
            <CopyButton value={code} label="Copy" />
        </div>
        <pre className="overflow-x-auto text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{code}</pre>
    </div>
);

// #region renderDocs
const renderDocs = () => (
    <div className="flex flex-col divide-y divide-zinc-200 dark:divide-zinc-700">
        <section className="pb-6">
            <h3 className="card-title">Authentication</h3>
            <p className="card-description mb-3">All external API requests authenticate with a brand API key secret sent as a bearer token. Each key is scoped to a single brand; requests act on behalf of that brand.</p>
            <CodeBlock title="Header" code={`Authorization: Bearer <secret>`} />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Base URL: <code>{externalBaseUrl}</code></p>
        </section>

        <section className="py-6">
            <h3 className="card-title">POST /designs</h3>
            <p className="card-description mb-3">Create a design in the brand&apos;s account. Send the image either as JSON with <code>image_url</code>, or as a multipart <code>image</code> file upload (stored server-side). A preview is uploaded and design analysis (colors + vision) runs automatically.</p>
            <div className="space-y-3">
                <CodeBlock title="Request body (JSON)" code={`{
  "title": "My Design",
  "image_url": "https://example.com/art.png",
  "collection_id": 123,        // optional
  "pattern_type": "half-drop"  // optional
}`} />
                <CodeBlock title="cURL (image_url)" code={`curl -X POST "${externalBaseUrl}/designs" \\
  -H "Authorization: Bearer <secret>" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"My Design","image_url":"https://example.com/art.png"}'`} />
                <CodeBlock title="cURL (file upload)" code={`curl -X POST "${externalBaseUrl}/designs" \\
  -H "Authorization: Bearer <secret>" \\
  -F "title=My Design" \\
  -F "image=@/path/to/art.png"`} />
                <CodeBlock title="JavaScript (fetch)" code={`await fetch("${externalBaseUrl}/designs", {
  method: "POST",
  headers: {
    "Authorization": "Bearer <secret>",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ title: "My Design", image_url: "https://example.com/art.png" })
});`} />
                <CodeBlock title="Python (requests)" code={`import requests

requests.post(
    "${externalBaseUrl}/designs",
    headers={"Authorization": "Bearer <secret>"},
    json={"title": "My Design", "image_url": "https://example.com/art.png"},
)`} />
            </div>
        </section>

        <section className="pt-6">
            <h3 className="card-title">GET /designs and GET /designs/:id</h3>
            <p className="card-description mb-3">List the brand&apos;s designs, or fetch a single design by id. A design belonging to another brand returns 404.</p>
            <CodeBlock title="cURL" code={`curl "${externalBaseUrl}/designs" -H "Authorization: Bearer <secret>"
curl "${externalBaseUrl}/designs/123" -H "Authorization: Bearer <secret>"`} />
        </section>
    </div>
);

// #region ApiDocs
const ApiDocs: React.FunctionComponent = (): React.ReactElement => {
    // undefined = still loading; null = fetch/patch failed (fall back to specUrl).
    const [spec, setSpec] = React.useState<string | null | undefined>(undefined);

    React.useEffect(() => {
        document.title = "API Documentation · Wallmates";
        let cancelled = false;
        buildEnvSpec()
            .then(s => { if (!cancelled) setSpec(s); })
            .catch(() => { if (!cancelled) setSpec(null); });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="h-screen w-full overflow-y-auto bg-surface text-gray-900 dark:text-gray-100">
            {spec === undefined
                ? <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading interactive API reference…</p>
                : spec
                    ? <ScalarDocs spec={spec} fallback={renderDocs()} />
                    : <ScalarDocs specUrl={SPEC_URL} fallback={renderDocs()} />}
        </div>
    );
};

export default ApiDocs;
